import { Component, Input, OnInit, input, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { AlertService } from 'src/app/services/alert.service';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, filter, switchMap } from 'rxjs';
import { combineLatest } from 'rxjs';
import { DeleteModalComponent } from 'src/app/components/shared/delete-modal/delete-modal.component';
import { WarningModalComponent } from 'src/app/components/shared/warning-modal/warning-modal.component';
import {
  EnhancementVariantDetail,
  IEnhancementVariantAsset,
  IMasterSuiteTouch,
  IMasterSuiteTouchAsset,
} from 'src/app/models/mastersuites/mastersuite';
import { MasterSuitesService } from 'src/app/services/mastersuites.service';
import { environment } from 'src/environments/environment';

interface TouchAssetDisplayInfo {
  name: string;
  rawUri: string | null;
  reviewUri: string | null;
  thumbnailUri: string | null;
}

@Component({
    selector: 'app-enhancement-variant-content',
    templateUrl: './enhancement-variant-content.component.html',
    styleUrls: ['./enhancement-variant-content.component.scss'],
    changeDetection: ChangeDetectionStrategy.Default,
    standalone: false
})
export class EnhancementVariantContentComponent implements OnInit {
  @Input() colPerScreen: number = 3;
  breakpoint = 1;
  masterSuiteId: string = '';
  enhancementVariants!: EnhancementVariantDetail[];
  private destroy$ = new Subject<void>();
  public enhancementVariantName = new FormControl('', [Validators.required]);
  apiBase = environment.apiBase;

  constructor(
    private masterSuitesService: MasterSuitesService,
    private activatedRoute: ActivatedRoute,
    public dialog: MatDialog,
    private router: Router,
    private alertService: AlertService,
  ) {
    this.masterSuiteId = this.activatedRoute.snapshot.paramMap.get('masterSuiteId') || '';
  }

  ngOnInit(): void {
    this.onSettingBreakPoint(window.innerWidth);
    this.getListEnhancementVariant();
  }

  private buildAssetName(touchIndex: number, assetType: string, typeIndex: number): string {
    const typeLabel = assetType === 'Still' ? 'Angle' : 'Video';
    return `Touch ${touchIndex + 1} / ${typeLabel} ${typeIndex}`;
  }

  private buildTouchAssetDisplayMap(
    masterSuiteTouches: IMasterSuiteTouch[],
  ): Map<string, TouchAssetDisplayInfo> {
    return masterSuiteTouches.reduce((map, touch, touchIndex) => {
      const stillAssets = touch.masterSuiteTouchAssets.filter(
        (a) => a.packageAssetType === 'Still',
      );
      const videoAssets = touch.masterSuiteTouchAssets.filter(
        (a) => a.packageAssetType === 'Video',
      );
      touch.masterSuiteTouchAssets.forEach((asset) => {
        const typeGroup = asset.packageAssetType === 'Video' ? videoAssets : stillAssets;
        const typeIndex = typeGroup.indexOf(asset) + 1;
        const name = this.buildAssetName(touchIndex, asset.packageAssetType, typeIndex);
        const uris: Pick<TouchAssetDisplayInfo, 'rawUri' | 'reviewUri' | 'thumbnailUri'> = {
          rawUri: asset.shootAssetPlaceholderRawUri,
          reviewUri: asset.shootAssetPlaceholderReviewUri,
          thumbnailUri: asset.shootAssetPlaceholderThumbnailUri,
        };
        map.set(asset.id!, { name, ...uris });
        (asset.copies ?? []).forEach((copy, copyIndex) => {
          map.set(copy.id!, { name: `${name}.${copyIndex + 1}`, ...uris });
        });
      });
      return map;
    }, new Map<string, TouchAssetDisplayInfo>());
  }

  private applyDisplayInfo(
    touchAsset: IEnhancementVariantAsset,
    displayMap: Map<string, TouchAssetDisplayInfo>,
  ): void {
    const info = displayMap.get(touchAsset.masterSuiteTouchAssetId);
    if (info) {
      touchAsset.assetName = info.name;
      touchAsset.shootAssetPlaceholderRawUri = info.rawUri;
      touchAsset.shootAssetPlaceholderReviewUri = info.reviewUri;
      touchAsset.shootAssetPlaceholderThumbnailUri = info.thumbnailUri;
    }
  }

  getListEnhancementVariant() {
    combineLatest([
      this.masterSuitesService.getAllEnhancementVariantOfMasterSuite(this.masterSuiteId),
      this.masterSuitesService.currentMasterSuite,
    ]).subscribe(([resEnhancementVariant, masterSuite]) => {
      const displayMap = this.buildTouchAssetDisplayMap(masterSuite.masterSuiteTouches);
      this.enhancementVariants = resEnhancementVariant.enhancementVariants;
      this.enhancementVariants.forEach((variant) => {
        variant.masterSuiteEnhancementVariantAssets.forEach((touchAsset) => {
          this.applyDisplayInfo(touchAsset, displayMap);
        });
      });
    });
  }

  onResize(event: any) {
    this.onSettingBreakPoint(event.target.innerWidth);
  }

  onSettingBreakPoint(innerWidth: number) {
    switch (true) {
      case innerWidth >= 2560:
        this.breakpoint = 8;
        break;

      case innerWidth >= 2260:
        this.breakpoint = 7;
        break;

      case innerWidth >= 1960:
        this.breakpoint = 6;
        break;

      case innerWidth >= 1660:
        this.breakpoint = 5;
        break;

      case innerWidth >= 1360:
        this.breakpoint = 4;
        break;

      case innerWidth >= 1060:
        this.breakpoint = 3;
        break;

      case innerWidth >= 760:
        this.breakpoint = 2;
        break;

      default:
        this.breakpoint = 1;
        break;
    }
  }

  editEnhancementVariant(enhancementVariant: EnhancementVariantDetail) {
    this.router.navigate([this.router.url, 'enhancement-variant', enhancementVariant.id]);
  }

  deleteEnhancementVariant(enhancementVariant: EnhancementVariantDetail) {
    const dialogRef = this.dialog.open(DeleteModalComponent, {
      data: {
        header: 'Delete',
        message: `Are you sure you want to delete this Enhancement Variant?`,
      },
    });
    dialogRef
      .afterClosed()
      .pipe(
        filter((confirmed) => !!confirmed),
        switchMap(() =>
          this.masterSuitesService.deleteEnhancementVariantById(enhancementVariant.id),
        ),
      )
      .subscribe({
        next: () => {
          this.enhancementVariants = this.enhancementVariants.filter(
            (e) => e.id !== enhancementVariant.id,
          );
          this.alertService.successToast(`Delete ${enhancementVariant.name} successfully!`);
        },
        error: () => {},
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
